import { default as isRowSortKeyEqual } from "./helpers/isSortKeyEqual";
import isMessageListOutOfOrder from "./helpers/isMessageListOutOfOrder";
import shouldSkipProcessRow from "./helpers/shouldSkipProcessRow";
import shouldSkipProcessReadTombstonedRow from "./helpers/shouldSkipProcessReadTombstonedRow";
import { setTotalRowsInViewMutator } from "./addRowResponse";
import fetchRichContentForRows from "./helpers/fetchRichContentForRows";
import showPostSyncNotification from "./showPostSyncNotification";
import { logVerboseUsage, logUsage } from "owa-analytics";
import TableOperations from "owa-mail-list-table-operations";
import { errorThatWillCauseAlert } from "owa-trace";
import type { MailListRowDataType, TableView, MailFolderTableQuery } from "owa-mail-list-store";
import { getMailboxInfo } from "owa-mail-mailboxinfo";
import { isGlobalFeatureEnabled } from "owa-feature-flags";
/* eslint-disable-next-line @typescript-eslint/no-restricted-imports  -- (https://aka.ms/OWALintWiki)
 * Transaction needed to fix performance problem from previous legacy actions */
import { transaction } from "owa-transaction";
import { getRowKeyFromListViewType, MailRowDataPropertyGetter } from "owa-mail-list-store";
import { getAccountByMailboxInfo } from "owa-account-source-list-store";
import folderIdToName from "owa-session-store/lib/utils/folderIdToName";
import type ReactListViewType from "owa-service/lib/contract/ReactListViewType";
import type { ConversationType, ItemRow } from "owa-graph-schema";
import { filterItemRowsForPriorityView, filterConversationRowsForPriorityView, } from "./helpers/priorityViewFilterUtils";
import syncClientTombstonedPropertiesToServerRow from "../internal/syncClientTombstonedPropertiesToServerRow";
import preserveExistingDraftInstanceKey from "./helpers/preserveExistingDraftInstanceKey";

/**
 * Process FindConversation/FindItem response and merge them to the top of the given TableView
 * @param rows to be merged
 * @param tableView to merge results in
 * @param totalRowsInViewInResponse - total rows in the table returned from server
 * @param removeRemainingRowsAfterMerge - flag indicating whether to remove the remaining rows after the merge
 * @param shouldLoadIsdataUptodate - flag indicating whether to log the isDateUptodate datapoint
 * @param source - the source of this action
 * @param isManualSync whether we are reloading as part of manual sync
 * @param showNotification whether to show post sync notification
 * @param hasNextPage indicates whether there is a next page
 * @param shouldHonorTombstonedReadStateOnMerge - flag to indicate if we should honor tombstoned read state when merging rows
 * @param shouldSyncTombstonedPropertiesOnMerge - flag to preserve client tombstoned properties
 * while merging the rest of the server row
 */
export default function mergeRowResponseFromTop(rows: MailListRowDataType[], tableView: TableView, totalRowsInViewInResponse: number, removeRemainingRowsAfterMerge: boolean, shouldLogIsDataUptodate: boolean, source: string, isManualSync: boolean = false, showNotification: boolean = false, hasNextPage: boolean = true, shouldHonorTombstonedReadStateOnMerge: boolean = false, shouldSyncTombstonedPropertiesOnMerge: boolean = false): void {
    if (!rows) {
        // VSO 1790: We may have no rows if the server returns an error like:
        // "The mailbox database is temporarily unavailable". Do not throw. Just trace this.
        errorThatWillCauseAlert("We need to have a valid list of rows");
        return;
    }
    
    const mailboxInfo = getMailboxInfo(tableView);
    
    if (shouldLogIsDataUptodate) {
        logVerboseUsage("TnS_MergeFindResponse", mailboxInfo, [isDataUpToDate(tableView, rows)]);
    }
    
    if (isManualSync && !isDataUpToDate(tableView, rows)) {
        const account = getAccountByMailboxInfo(mailboxInfo);
        
        logUsage("MailManualSync_OutOfDate", mailboxInfo, {
            mailboxType: mailboxInfo.type,
            accountType: account?.sourceType,
        });
    }
    
    let insertIndex = 0;
    let failedCount = 0;
    const listViewType = tableView.tableQuery.listViewType;
    const folderName = folderIdToName(tableView.tableQuery.folderId);
    
    if ((tableView?.tableQuery as MailFolderTableQuery)?.scenarioType === "priorityView" &&
        rows.length > 0) {
        if (listViewType === 1) {
            rows = filterItemRowsForPriorityView(rows as ItemRow[]);
        }
        else {
            rows = filterConversationRowsForPriorityView(rows as ConversationType[]);
        }
    }
    
    if (showNotification) {
        showPostSyncNotification(tableView, rows, listViewType === 0);
    }
    
    // We are calling multiple mutators in table operations in a for loop so we need to wrap
    // them in a transaction to avoid multiple re-renders
    const mergeAndCleanup = () => {
        for (const responseRow of rows) {
            const row = preserveExistingDraftInstanceKey(responseRow, tableView);
            // Check if row should be processed
            if (shouldSkipProcessRow(row, tableView)) {
                continue;
            }
            
            if (shouldSyncTombstonedPropertiesOnMerge) {
                syncClientTombstonedPropertiesToServerRow(row, tableView);
            }
            
            const rowKey = getRowKeyFromListViewType(row, listViewType);
            const currentIndex = tableView.rowKeys.indexOf(rowKey);
            
            // Since we are merging the rows from the server into the existing table, we want to honor any rows that have tombstone entries
            // for the Read reason. We want to skip processing these rows to prevent flickering of the read/unread states
            // in the UI during merge operations and also to prevent having the incorrect state stick.
            if (shouldHonorTombstonedReadStateOnMerge &&
                currentIndex >= 0 &&
                shouldSkipProcessReadTombstonedRow(row, tableView)) {
                insertIndex++; // increment insertIndex since this row is staying in place in the table
                continue;
            }
            
            let wasSuccessful = true;
            if (TableOperations.containsRowKey(tableView, rowKey)) {
                if (currentIndex < insertIndex) {
                    const isInstanceKeyEmpty = !rowKey;
                    /* eslint-disable-next-line owa-custom-rules/forbid-specific-functions-patterns-inside-loops -- (https://aka.ms/OWALintWiki)
                     * Baseline, this function can't be used inside a loop, please move it outside
                     *	> Function 'logUsage' matches forbidden pattern (/logUsage/) and should not be used inside loops */
                    logUsage("MergeRowResponseFromTop_DuplicateRowsInResponse", mailboxInfo, {
                        currentIndex,
                        insertIndex,
                        tableType: tableView.tableQuery.type,
                        listViewType,
                        folderName,
                        rowKey,
                        isInstanceKeyEmpty,
                        source,
                    });
                    // If we're moving an existing row to a position further down in the list (which can only happen in the case where the service returns multiple rows
                    // with the same InstanceKey), we decrease insertIndex by 1 to account for the row being removed from a position further up in the list prior to being inserted at its new position.
                    insertIndex--;
                }
                TableOperations.updateRowMutator(insertIndex, row, tableView, source, false, "RowInitialLoad");
            }
            else {
                // Otherwise add new row to the table
                wasSuccessful = TableOperations.addRowMutator(insertIndex, row, tableView, source, false, "RowInitialLoad");
            }
            
            // Increment index if the operation was successful else
            // always increment.
            if (wasSuccessful) {
                insertIndex++;
            }
            else {
                failedCount++;
            }
        }
        
        // VSO - 20814 - Add datapoint to log the freshness of cached table on switch mail folder
        // removeRemainingRowsAfterMerge would be true in case of ReloadTable.
        // For such scenario, it generally indicates that we're out of sync with server, so we need to invalidate
        // remaining items we had after merging the top items
        if (removeRemainingRowsAfterMerge) {
            const processedRowCount = insertIndex;
            const rowKeysLength = tableView.rowKeys.length;
            for (let i = rowKeysLength - 1; i >= processedRowCount; i--) {
                const rowKey = tableView.rowKeys[i];
                const rowId = MailRowDataPropertyGetter.getRowIdString(rowKey, tableView);
                const rowKeysForRowId: string[] = tableView.rowIdToRowKeyMap.get(rowId) ?? [];
                /* eslint-disable-next-line owa-custom-rules/forbid-specific-functions-patterns-inside-loops -- (https://aka.ms/OWALintWiki)
                 * Baseline, this function can't be used inside a loop, please move it outside
                 *	> Function 'logUsage' matches forbidden pattern (/logUsage/) and should not be used inside loops */
                logUsage("MergeRowResponseFromTop_RemoveRow", mailboxInfo, {
                    tableType: tableView.tableQuery.type,
                    listViewType,
                    folderName,
                    rowKey,
                    rowId,
                    rowKeysForRowIdHasItems: rowKeysForRowId.length > 0,
                });
                
                TableOperations.removeRow(rowKey, tableView, source + "_RemoveAfterMerge");
            }
        }
    };
    
    // When flighted, wrap all row mutations in a single MobX transaction to batch
    // reactions and avoid triggering N re-renders for N rows.
    if (isGlobalFeatureEnabled("fwk-loaf-mru-cache")) {
        transaction(mergeAndCleanup);
    }
    else {
        mergeAndCleanup();
    }
    
    // There have been a few occasions where existing rows that are meant to be in the top fetched rows are omitted from the query result due to other bugs that may happen.
    // When this happens, such rows get pushed down to insertIndex and below and are out of order. insertIndex is the index immediately after the last row inserted in this function.
    // We are checking the row at insertIndex to see if it is out of order compared to the row at the indexInsert - 1 index and we log this if that is the case.
    if (!removeRemainingRowsAfterMerge && isMessageListOutOfOrder(tableView, insertIndex)) {
        logUsage("MergeRowResponseFromTop_OutOfOrderItems", mailboxInfo, {
            tableType: tableView.tableQuery.type,
            listViewType,
            folderName,
            source,
        });
    }
    
    // Always update totalRowsInView after making a find request, to allow us to calculate whether this table can load more
    setTotalRowsInViewMutator(tableView, totalRowsInViewInResponse - failedCount, hasNextPage);
    
    fetchRichContentForRows(tableView);
}

function isDataUpToDate(tableView: TableView, rows: MailListRowDataType[]): boolean {
    // We only want to compare first 25 rows from the response to determine if
    // the data we have is up-to-date. Even though this is not 100% correct
    // it's a guesstimate that the data would probably be up-to-date if first rows are.
    for (let i = 0; i < Math.min(tableView.rowKeys.length, 25, rows.length); i++) {
        if (tableView.rowKeys[i] != rows[i].InstanceKey || !isRowSortKeyEqual(rows[i], tableView)) {
            return false;
        }
    }
    
    return true;
}
